import { graphql } from "gatsby";
import { get, head, map } from "lodash";
import React, { Fragment, useMemo, useState } from "react";
import { convert } from "html-to-text";
import BaseLayout from "../../layout/base/BaseLayout";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { AccessTime, ArrowBack } from "@mui/icons-material";
import { GatsbyImage } from "gatsby-plugin-image";
import Share from "../blog/post/Share";
import Seo from "../../components/Seo";
import { useMount } from "react-use";
import QueryPreservingLink from "../../components/QueryPreservingLink";

const Post = ({ data: { post, posts, advertisement } }) => {
  const {
    author,
    categories,
    content,
    date,
    featuredImage,
    title,
    slug,
    tags,
    databaseId,
  } = post;

  const category = head(categories.nodes);

  const [discourseUrl, setDiscourseUrl] = useState("");

  const contentWithBanner = useMemo(() => {
    return content.split("<p>{{ advertisement }}</p>");
  }, [content]);

  useMount(async () => {
    try {
      const res = await (
        await fetch(
          `${process.env.GATSBY_WP_DOMAIN}/wp-json/wp/v2/posts/${databaseId}`
        )
      ).json();

      setDiscourseUrl(res?.meta?.discourse_permalink);
    } catch (e) {
      console.error(e);
    }
  });

  return (
    <BaseLayout page={`blog/${slug}`}>
      <Stack
        sx={{
          padding: {
            xs: "46.8px 24px 103px 24px",
            md: "46.8px 54px 103px 54px",
            lg: "46.8px 143px 103px 143px",
          },
        }}
      >
        <Box
          component={QueryPreservingLink}
          to="/blog"
          sx={{
            mb: "32px",
            textDecoration: "none",
            color: "#6a27e8",
            fontWeight: "bold",
            fontSize: "14px",
            lineHeight: 1.2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ArrowBack sx={{ mr: "15px" }} />
          Back to blog
        </Box>

        <Stack sx={{ alignSelf: "center", maxWidth: "814.5px", width: "100%" }}>
          <Box
            component={GatsbyImage}
            sx={{
              height: "auto",
              objectFit: "contain",
            }}
            image={
              featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
            }
            alt={featuredImage?.node?.altText}
          />
          <Stack
            direction="row"
            sx={{
              marginTop: "48px",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 500,
                color: "#A2AAB5",
              }}
            >
              {date}
            </Typography>

            <Share url={`${process.env.GATSBY_SITE_URL}/blog/${slug}`} />
          </Stack>
          <Box
            sx={{
              fontWeight: 900,
              fontSize: "48px",
              color: "#3f119b",
              marginTop: "20px",
              lineHeight: 1.19,
            }}
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <Box
            sx={{
              my: "20px",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1.25,
              color: "#626972",
            }}
          >
            <span>by</span>

            <Box
              sx={{
                textDecoration: "none",
                color: "#6a27e8",
                mx: "4px",
              }}
              component={QueryPreservingLink}
              to={`/blog/author/${author?.node.slug}`}
            >
              {author?.node.name}
            </Box>

            <span>in</span>

            <Box
              sx={{
                textDecoration: "none",
                color: "#6a27e8",
                mx: "4px",
              }}
              component={QueryPreservingLink}
              to={`/blog/category/${category.slug}`}
            >
              {category.name}
            </Box>
          </Box>

          {discourseUrl && (
            <Box
              sx={{
                marginTop: "20px",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: 1.25,
                color: "#626972",
              }}
            >
              <span>Continue discussion on our</span>

              <Box
                sx={{
                  textDecoration: "none",
                  color: "#6a27e8",
                  mx: "4px",
                }}
                component={QueryPreservingLink}
                to={discourseUrl}
                target="_blank"
              >
                Forum
              </Box>
            </Box>
          )}

          {contentWithBanner.map((content, index) => (
            <Fragment key={index}>
              {index !== 0 && (
                <QueryPreservingLink
                  to={advertisement.data.url.url}
                  target="_blank"
                >
                  <Box
                    component="img"
                    src={advertisement.data.image.url}
                    sx={{ width: "100%", objectFit: "cover" }}
                  />
                </QueryPreservingLink>
              )}

              <Box
                className="html-content"
                dangerouslySetInnerHTML={{ __html: content }}
                sx={{
                  fontSize: "24px",
                  lineHeight: 1.33,
                  marginTop: "18.2px",
                  color: "#626972",
                  "& a": {
                    color: "#6a27e8",
                  },
                  "& .wp-block-code": {
                    backgroundColor: "#f4f4f4",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    padding: "10px!important",
                    overflow: "auto",
                    fontFamily: "'Courier New', monospace",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    display: "flex",
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    objectFit: "contain",
                  },
                }}
              />
            </Fragment>
          ))}

          <Stack
            direction="row"
            sx={{
              marginTop: "40px",
              alignItems: "start",
              flexWrap: "wrap",
            }}
          >
            {map(tags.nodes, ({ id, name, slug }) => (
              <Stack
                component={QueryPreservingLink}
                key={`category-${id}`}
                to={`/blog/tag/${slug}`}
                sx={{
                  minWidth: "50px",
                  justifyContent: "center",
                  alignItems: "center",
                  textDecoration: "none",
                  marginRight: "5px",
                  fontSize: "18px",
                  height: "34.1px",
                  border: "none",
                  backgroundColor: "rgba(162, 170, 181, 0.35)",
                  borderRadius: "5px",
                  color: "#626972",
                  padding: "0 26.4px",
                  marginBottom: "8px",
                }}
              >
                {name}
              </Stack>
            ))}
          </Stack>

          {discourseUrl && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              sx={{ fontSize: 14, mt: 2, alignSelf: "start" }}
              component={QueryPreservingLink}
              to={discourseUrl}
              target="_blank"
            >
              Add a comment
            </Button>
          )}

          <Divider sx={{ marginTop: "52px" }} />

          <Stack sx={{ pt: "32px" }}>
            <Typography
              sx={{
                color: "#2a149a",
                fontSize: "24px",
                fontWeight: 900,
                mb: "28px",
              }}
            >
              Recent Posts
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              {map(
                posts.edges,
                ({ node: { id, title, date, slug, featuredImage } }) => {
                  return (
                    <Paper
                      component={QueryPreservingLink}
                      to={`/blog/${slug}`}
                      key={id}
                      sx={{
                        boxShadow:
                          "-10px 11.1px 50px 0 rgba(188, 198, 210, 0.33)",
                        borderRadius: "10px",
                        padding: "23px",
                        display: "flex",
                        flexDirection: "column",
                        textDecoration: "none",
                        flex: 1,
                      }}
                    >
                      <GatsbyImage
                        image={
                          featuredImage?.node?.localFile?.childImageSharp
                            ?.gatsbyImageData
                        }
                        alt={featuredImage?.node?.altText}
                      />

                      <Stack
                        sx={{
                          marginTop: "26px",
                          alignItems: "center",
                        }}
                        direction={"row"}
                      >
                        <AccessTime
                          sx={{
                            color: "#A2AAB5",
                            marginRight: "7px",
                            fontSize: "14px",
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#A2AAB5",
                          }}
                        >
                          {date}
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          lineHeight: 1.29,
                          maxWidth: "278px",
                          fontSize: "14px",
                          fontWeight: 900,
                          color: "#2a149a",
                          marginTop: "7px",
                        }}
                      >
                        {title}
                      </Typography>

                      <Typography
                        sx={{
                          marginTop: "10px",
                          color: "#6a27e8",
                          fontSize: "14px",
                          fontWeight: "bold",
                          textDecoration: "none",
                        }}
                      >
                        See post
                      </Typography>
                    </Paper>
                  );
                }
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </BaseLayout>
  );
};

export default Post;

export const Head = (queryResult) => {
  const meta = get(queryResult, ["data", "prismicPage", "data", "meta"], {});
  const canonical = get(queryResult, [
    "data",
    "post",
    "acfSeo",
    "canonicalUrl",
  ]);

  const imageUrl = get(
    queryResult,
    [
      "data",
      "post",
      "featuredImage",
      "node",
      "localFile",
      "childImageSharp",
      "gatsbyImageData",
      "src",
    ],
    ""
  );

  const { excerpt, title, tags, slug } = get(queryResult, ["data", "post"], "");

  const parsedExcerpt = excerpt
    ? convert(excerpt, {
        wordwrap: 130,
      })
    : "";

  return (
    <Seo
      meta={meta}
      canonical={canonical}
      hardcodedMeta={[
        { name: "image", content: imageUrl },
        { name: "description", content: parsedExcerpt },
        { name: "keywords", content: map(tags.nodes, "name") },
        {
          name: "og:url",
          content: `${process.env.GATSBY_SITE_URL}/blog/${slug}`,
        },
        { name: "og:type", content: "article" },
        { name: "og:title", content: title },
        { name: "og:description", content: parsedExcerpt },
        {
          name: "og:image",
          content: imageUrl,
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: parsedExcerpt },
        { name: "twitter:image", content: imageUrl },
      ]}
    />
  );
};

export const pageQuery = graphql`
  query PostPageQuery($id: String!) {
    advertisement: prismicAdvertisement {
      id
      data {
        image {
          url
          alt
        }
        url {
          url
        }
      }
    }
    post: wpPost(id: { eq: $id }) {
      id
      databaseId
      excerpt
      link
      commentStatus
      title
      content
      slug
      date(formatString: "MMMM DD, YYYY")
      author {
        node {
          name
          url
          slug
          description
        }
      }
      categories {
        nodes {
          id
          slug
          name
        }
      }
      tags {
        nodes {
          id
          slug
          name
        }
      }
      acfSeo {
        canonicalUrl
      }
      seo {
        metaDesc
      }
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
      }
    }
    posts: allWpPost(limit: 3) {
      edges {
        node {
          id
          title
          date(formatString: "MMMM DD, YYYY")
          slug
          featuredImage {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(aspectRatio: 1.5)
                }
              }
            }
          }
        }
      }
    }
  }
`;
